import { useState, useEffect, useCallback } from "react";
import { T } from "./theme";
import { Plus, Target, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp, Trash2, RefreshCw, Sparkles, AlertTriangle, Timer } from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";
import Input from "./components/Input";

const STATUS_CONFIG = {
  planejando: { label:"Planejando", color:T.accent,  icon:Clock        },
  fazendo:    { label:"Fazendo",    color:T.amber,   icon:AlertCircle  },
  estudando:  { label:"Estudando",  color:"#c084fc", icon:Target       },
  agindo:     { label:"Agindo",     color:T.green,   icon:CheckCircle  },
};
const AREAS = ["Exames","Internação","Pronto Atendimento","Maternidade","Centro Médico","Geral"];

function PrioridadeBadge({ p }) {
  const cfg = { alta:{c:T.red,l:"Alta"}, media:{c:T.amber,l:"Média"}, baixa:{c:T.green,l:"Baixa"} };
  const {c,l} = cfg[p]||cfg.media;
  return <Badge color={c}>{l}</Badge>;
}
function IABadge() {
  return <Badge color="#c084fc"><Sparkles size={9}/> IA</Badge>;
}

function CicloCard({ ciclo, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(ciclo._novo||false);
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState(ciclo);
  const [saving, setSaving]     = useState(false);
  const StatusIcon = STATUS_CONFIG[ciclo.status]?.icon||Clock;
  const cor = STATUS_CONFIG[ciclo.status]?.color||T.accent;
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    await fetch(`/api/pdsa/${ciclo.id}`,{method:"PUT",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(form)});
    onUpdate(form); setEditing(false); setSaving(false);
  };

  return (
    <Card noPadding style={{border:`1px solid ${ciclo._novo?"rgba(192,132,252,0.4)":T.border}`,marginBottom:12}}>
      <div style={{padding:"16px 20px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}} onClick={()=>setExpanded(e=>!e)}>
        <div style={{width:36,height:36,borderRadius:10,background:`${cor}18`,border:`1px solid ${cor}30`,display:"flex",alignItems:"center",justifyContent:"center",color:cor,flexShrink:0}}><StatusIcon size={16}/></div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:600,fontSize:14,color:T.textHi,display:"flex",alignItems:"center",gap:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {ciclo.titulo} {ciclo._origem==="wizard"&&<IABadge/>}
          </div>
          <div style={{fontSize:11,color:T.textLow,marginTop:2,display:"flex",gap:8,alignItems:"center"}}>
            <span style={{padding:"1px 7px",borderRadius:99,background:T.accentDim,color:T.accent,fontSize:10}}>{ciclo.area}</span>
            <span style={{color:cor}}>{STATUS_CONFIG[ciclo.status]?.label}</span>
            <span>Prazo: {ciclo.prazo||"—"}</span>
            {ciclo.roi_estimado&&<span style={{color:T.green,fontSize:10}}>ROI: {ciclo.roi_estimado}</span>}
          </div>
        </div>
        <PrioridadeBadge p={ciclo.prioridade}/>
        <div style={{color:T.textLow,flexShrink:0}}>{expanded?<ChevronUp size={16}/>:<ChevronDown size={16}/>}</div>
      </div>

      {expanded&&(
        <div style={{padding:"0 20px 20px",borderTop:`1px solid ${T.border}`}}>
          <div style={{margin:"16px 0 12px"}}>
            <div style={{fontSize:11,fontWeight:600,color:T.textLow,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.08em"}}>Meta</div>
            {editing?<Input value={form.meta||""} onChange={e=>set("meta",e.target.value)} style={{fontSize:12}}/>:<p style={{margin:0,fontSize:13,color:T.textMid,lineHeight:1.6}}>{ciclo.meta||"—"}</p>}
          </div>
          {ciclo._origem==="wizard"&&ciclo.urgencia&&(
            <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
              <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,padding:"3px 10px",borderRadius:99,background:"rgba(251,191,36,0.1)",color:T.amber,border:"1px solid rgba(251,191,36,0.2)"}}><Timer size={10}/> {ciclo.urgencia}</span>
              {(ciclo.benchmarks||[]).map((b,i)=><span key={i} style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:"rgba(56,189,248,0.08)",color:T.blue,border:"1px solid rgba(56,189,248,0.15)"}}>{b}</span>)}
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:16}}>
            {[["Plan",T.accent,"plan"],["Do",T.amber,"doText"],["Study","#c084fc","study"],["Act",T.green,"act"]].map(([label,c,key])=>(
              <div key={key} style={{background:T.bgBase,borderRadius:8,padding:12,borderTop:`2px solid ${c}`}}>
                <div style={{fontSize:11,fontWeight:700,color:c,marginBottom:8,letterSpacing:"0.08em",textTransform:"uppercase"}}>{label}</div>
                {editing?<Input textarea value={form[key]||""} onChange={e=>set(key,e.target.value)} style={{minHeight:80,fontSize:12}}/>:<p style={{fontSize:12,color:ciclo[key]?T.textMid:T.textMid,margin:0,lineHeight:1.6}}>{ciclo[key]||"—"}</p>}
              </div>
            ))}
          </div>
          {ciclo._origem==="wizard"&&(ciclo.riscos||[]).length>0&&!editing&&(
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:600,color:T.textLow,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.08em"}}><AlertTriangle size={11}/> Riscos (IA)</div>
              {ciclo.riscos.map((r,i)=><div key={i} style={{fontSize:12,color:"#fca5a5",padding:"6px 12px",background:"rgba(239,68,68,0.06)",borderRadius:6,marginBottom:4,borderLeft:"2px solid rgba(239,68,68,0.3)"}}>{r}</div>)}
            </div>
          )}
          {editing&&(
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
              {Object.entries(STATUS_CONFIG).map(([k,cfg])=>(
                <button key={k} onClick={()=>set("status",k)} style={{padding:"6px 14px",borderRadius:99,fontSize:12,fontWeight:600,cursor:"pointer",background:form.status===k?`${cfg.color}25`:"transparent",border:`1px solid ${form.status===k?cfg.color:T.border}`,color:form.status===k?cfg.color:T.textLow}}>{cfg.label}</button>
              ))}
            </div>
          )}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Button variant="ghost" size="sm" icon={Trash2} onClick={()=>onDelete(ciclo.id)} style={{color:T.red}}>Excluir</Button>
            {editing?<Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving?"Salvando...":"Salvar"}</Button>:<Button variant="secondary" size="sm" onClick={()=>setEditing(true)}>Editar</Button>}
          </div>
        </div>
      )}
    </Card>
  );
}

function NovoModal({ onSave, onClose }) {
  const [form, setForm] = useState({titulo:"",area:"Exames",status:"planejando",prioridade:"media",meta:"",plan:"",doText:"",study:"",act:"",prazo:""});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const selectStyle = { background:T.bgInput, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 12px", color:T.textHi, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box" };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:T.radius,padding:28,width:"min(620px,95vw)",maxHeight:"90vh",overflowY:"auto",boxShadow:T.shadowLg}}>
        <h2 style={{margin:"0 0 20px",fontSize:18,fontWeight:700,color:T.textHi}}>Novo Ciclo PDSA</h2>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Input placeholder="Título do ciclo" value={form.titulo} onChange={e=>set("titulo",e.target.value)} style={{fontSize:15}}/>
          <div style={{display:"flex",gap:10}}>
            <select value={form.area} onChange={e=>set("area",e.target.value)} style={{...selectStyle,flex:1}}>
              {AREAS.map(a=><option key={a} value={a}>{a}</option>)}
            </select>
            <select value={form.prioridade} onChange={e=>set("prioridade",e.target.value)} style={{...selectStyle,flex:1}}>
              <option value="alta">Alta prioridade</option>
              <option value="media">Média prioridade</option>
              <option value="baixa">Baixa prioridade</option>
            </select>
            <Input type="date" value={form.prazo} onChange={e=>set("prazo",e.target.value)} style={{flex:1}}/>
          </div>
          <Input placeholder="Meta (ex: Aumentar NPS de 60 para 80 em PA)" value={form.meta} onChange={e=>set("meta",e.target.value)}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[["plan","Plan — O que vamos testar?",T.accent],["doText","Do — Como vamos executar?",T.amber],["study","Study — Como vamos medir?","#c084fc"],["act","Act — O que faremos depois?",T.green]].map(([k,ph,c])=>(
              <Input key={k} textarea placeholder={ph} value={form[k]} onChange={e=>set(k,e.target.value)} style={{minHeight:80,borderTop:`2px solid ${c}`}}/>
            ))}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:20}}>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={()=>form.titulo&&onSave(form)}>Criar Ciclo</Button>
        </div>
      </div>
    </div>
  );
}

export default function PDSA({ cicloNovo }) {
  const [ciclos, setCiclos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroArea, setFiltroArea]     = useState("todas");

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await fetch("/api/pdsa",{credentials:"include"});
    const d = await r.json();
    setCiclos(d.ciclos||[]);
    setLoading(false);
  },[]);

  useEffect(()=>{ carregar(); },[carregar]);
  useEffect(()=>{ if(cicloNovo) setCiclos(cs=>cs.find(c=>c.id===cicloNovo.id)?cs:[{...cicloNovo,_novo:true},...cs]); },[cicloNovo]);

  const adicionar = async (c) => {
    const r = await fetch("/api/pdsa",{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(c)});
    const d = await r.json();
    setCiclos(cs=>[d.ciclo,...cs]); setModal(false);
  };
  const atualizar = (c) => setCiclos(cs=>cs.map(x=>x.id===c.id?c:x));
  const excluir = async (id) => { await fetch(`/api/pdsa/${id}`,{method:"DELETE",credentials:"include"}); setCiclos(cs=>cs.filter(x=>x.id!==id)); };

  const filtrados = ciclos.filter(c=>(filtroStatus==="todos"||c.status===filtroStatus)&&(filtroArea==="todas"||c.area===filtroArea));
  const contadores = Object.fromEntries(Object.keys(STATUS_CONFIG).map(k=>[k,ciclos.filter(c=>c.status===k).length]));

  return (
    <PageContainer>
      <PageHeader icon={RefreshCw} title="Ciclos PDSA" description="Plan · Do · Study · Act — Melhoria contínua baseada em dados NPS">
        <Button variant="secondary" icon={RefreshCw} onClick={carregar}>Atualizar</Button>
        <Button variant="primary" icon={Plus} onClick={()=>setModal(true)}>Novo Ciclo</Button>
      </PageHeader>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:24}}>
        {Object.entries(STATUS_CONFIG).map(([k,cfg])=>{
          const Icon=cfg.icon;
          return (
            <Card key={k} noPadding style={{background:filtroStatus===k?`${cfg.color}15`:T.bgCard,border:`1px solid ${filtroStatus===k?cfg.color:T.border}`,cursor:"pointer"}} onClick={()=>setFiltroStatus(filtroStatus===k?"todos":k)}>
              <div style={{padding:"16px 18px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><Icon size={14} color={cfg.color}/><span style={{fontSize:11,color:cfg.color,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{cfg.label}</span></div>
                <div style={{fontSize:32,fontWeight:800,fontFamily:T.fontTitle,color:cfg.color}}>{contadores[k]||0}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {["todas",...AREAS].map(a=>(
          <button key={a} onClick={()=>setFiltroArea(a)} style={{padding:"5px 14px",borderRadius:99,fontSize:12,cursor:"pointer",background:filtroArea===a?T.accentDim:"transparent",border:`1px solid ${filtroArea===a?T.accent:T.border}`,color:filtroArea===a?T.accent:T.textLow}}>
            {a==="todas"?"Todas as áreas":a}
          </button>
        ))}
      </div>

      {loading?<PageLoading label="Carregando ciclos..."/>
        :filtrados.length===0?<div style={{textAlign:"center",color:T.textMid,padding:60,fontSize:14}}>{ciclos.length===0?"Nenhum ciclo criado ainda. Use o Wizard PDSA ou crie manualmente.":"Nenhum ciclo encontrado com esses filtros."}</div>
        :filtrados.map(c=><CicloCard key={c.id} ciclo={c} onUpdate={atualizar} onDelete={excluir}/>)}
      {modal&&<NovoModal onSave={adicionar} onClose={()=>setModal(false)}/>}
    </PageContainer>
  );
}
